export type CommuneDB = {
  codeInsee: string;
  nom: string;
  codePostal: string;
  latitude: number;
  longitude: number;
};

export type CoordinatesArray = [number, number];

export type CommuneEntity = CommuneDB & { type: 'commune' };

export type DepartementEntity = {
  nom: string;
  code: string;
  geometry: {
    type: 'Polygon';
    coordinates: [CoordinatesArray[]];
  };
  type: 'departement';
};

/** arrondissement municipal de Paris, Lyon ou Marseille (ni coordonnées ni code postal fournis par l'API) */
export type ArrondissementEntity = {
  nom: string;
  /** code INSEE de l'arrondissement (ex. 75115) */
  code: string;
  /** code INSEE de la commune (ex. 75056) */
  codeParent: string;
  geometry: {
    type: 'Polygon';
    coordinates: [CoordinatesArray[]];
  };
  type: 'arrondissement';
};

export type GeocodedAddress = {
  fullAddress: string;
  housenumber: string;
  addressLine: string;
  postCode: string;
  inseeCode: string;
  city: string;
  street: string;
  lat: number;
  lng: number;
};

export type GeoEntity = CommuneEntity | DepartementEntity | ArrondissementEntity;

export type GeoEntities = GeoEntity[];

export const searchEntity = async (query: string, params?: Record<string, string>): Promise<GeoEntities> => {
  const searchParams = new URLSearchParams({ q: query, ...params })
  const res = await fetch(`${process.env.NEXT_PUBLIC_GEO_API_URL}/places?${searchParams}`)
  const data: GeoEntities = await res.json();

  // arrondissements triés par numéro, à la place qu'ils occupent dans les résultats :
  // le code INSEE suit l'ordre des arrondissements dans chaque ville (69381 → Lyon 1er, 69382 → Lyon 2e…)
  const arrondissements = data
    .filter(entity => entity.type === 'arrondissement')
    .sort((a, b) => Number(a.code) - Number(b.code))

  return data.map(entity => entity.type === 'arrondissement' ? arrondissements.shift()! : entity)
}


// villes à arrondissements, indexées par les 2 premiers chiffres du code INSEE
const ARRONDISSEMENT_CITIES: Record<string, string> = {'75': 'Paris', '69': 'Lyon', '13': 'Marseille'}

/** retrouve des arrondissements à partir de leurs codes INSEE (l'API ne cherche pas par code, on interroge par ville) */
export const getArrondissements = async (codes: string[]): Promise<ArrondissementEntity[]> => {
  const cities = [...new Set(codes.map(code => ARRONDISSEMENT_CITIES[code.slice(0, 2)]).filter(Boolean))]
  const results = await Promise.all(cities.map(city => searchEntity(city, { placesTypes: 'arrondissement' })))

  return results.flat()
    .filter((entity): entity is ArrondissementEntity => entity.type === 'arrondissement' && codes.includes(entity.code))
}

/** codes INSEE des communes découpées en arrondissements : Paris, Lyon, Marseille */
const ARRONDISSEMENT_CITY_CODES = ['75056', '69123', '13055']

export const isArrondissementCity = (codeInsee?: string) => !!codeInsee && ARRONDISSEMENT_CITY_CODES.includes(codeInsee)

/** codes INSEE des arrondissements municipaux : Paris 75101-75120, Lyon 69381-69389, Marseille 13201-13216 */
export const isArrondissementCode = (code?: string) =>
  !!code && /^(751(0[1-9]|1\d|20)|6938[1-9]|132(0[1-9]|1[0-6]))$/.test(code)

/**
 * localité par défaut d'une page ville : un arrondissement est cherché par son contour (sans rayon),
 * une commune par son centre et un rayon.
 */
export const getCityLocality = async (city: { codeInsee?: string | null, ville?: string | null, codePostal?: string | null, lat?: number | null, lng?: number | null }): Promise<GeoEntity> => {
  if (city.codeInsee && isArrondissementCode(city.codeInsee)) {
    try {
      const [arrondissement] = await getArrondissements([city.codeInsee as string])
      if (arrondissement) return arrondissement
    } catch (err) {
      console.warn(`⚠️ Contour introuvable pour l'arrondissement ${city.codeInsee}, recherche par rayon`, err)
    }
  }
  return {
    codeInsee: city.codeInsee as string,
    nom: city.ville as string,
    codePostal: city.codePostal as string,
    latitude: city.lat as number,
    longitude: city.lng as number,
    type: 'commune',
  }
}

// premier code INSEE d'arrondissement - 1, par département : 75101 → Paris 1er, 69381 → Lyon 1er, 13201 → Marseille 1er
const ARRONDISSEMENT_CODE_BASES: Record<string, number> = {'75': 75100, '69': 69380, '13': 13200}

/**
 * champs postaux d'une commune ou d'un arrondissement, pour l'envoi des formulaires.
 * L'API ne fournit ni code postal ni coordonnées pour un arrondissement : code postal déduit
 * de son numéro (75115 → 75015, 69381 → 69001) et coordonnées au centre de son contour.
 */
export const getPostalLocality = (entity?: GeoEntity | null) => {
  if (entity?.type === 'arrondissement') {
    const dep = entity.code.slice(0, 2)
    const ring = entity.geometry.coordinates[0]
    return {
      ville: entity.nom,
      codePostal: `${dep}0${String(Number(entity.code) - ARRONDISSEMENT_CODE_BASES[dep]).padStart(2, '0')}`,
      codeInsee: entity.code,
      latitude: ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length,
      longitude: ring.reduce((sum, [lng]) => sum + lng, 0) / ring.length,
    }
  }
  return {
    ville: entity?.type === 'commune' ? entity.nom : '',
    codePostal: entity?.type === 'commune' ? entity.codePostal : '',
    codeInsee: entity?.type === 'commune' ? entity.codeInsee : '',
    latitude: entity?.type === 'commune' ? entity.latitude : 0,
    longitude: entity?.type === 'commune' ? entity.longitude : 0,
  }
}

/** point dans un contour GeoJSON [lng, lat] (lancer de rayon), équivalent de l'insidePolygon d'Algolia */
export const isInsideGeometry = (lat: number, lng: number, geometry: ArrondissementEntity['geometry']) => {
  const ring = geometry.coordinates[0]
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** contour GeoJSON [lng, lat] converti en [lat, lng] pour Algolia et Leaflet */
export const toLatLngPolygon = (geometry: DepartementEntity['geometry']) =>
  geometry.coordinates[0].map((curr: number[]) => [curr[1], curr[0]])

/**
 * polygones de filtrage Algolia : uniquement les arrondissements (sans facette dédiée).
 * Les départements sont filtrés par la facette codeDepartement, exacte, leur contour ne sert qu'à l'affichage.
 */
export const getSearchPolygons = (localities: GeoEntity[]): number[][][] | undefined => {
  if (localities.length > 0 && localities.every(loc => loc.type === 'arrondissement')) {
    return (localities as ArrondissementEntity[]).map(loc => toLatLngPolygon(loc.geometry))
  }
  return undefined
}

/** contours affichés sur la carte : départements et arrondissements sélectionnés */
export const getLocalitiesOutlines = (localities: GeoEntity[]): number[][][] =>
  localities.flatMap(loc => loc.type === 'commune' ? [] : [toLatLngPolygon(loc.geometry)])

export const getDepartementCodes = (localities: GeoEntity[]): string[] =>
  localities.flatMap(loc => loc.type === 'departement' ? [loc.code] : [])

export const searchAddress = async (query: string): Promise<GeocodedAddress[]> => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_GEO_API_URL}/address_geocode?q=${query}`)
  const data = await res.json();

  return data
}

export const getDepGeo = async (code: string) => {
  const data = (await import('../../data/departments.json')).default as unknown as {
    features: {
      properties: {
        code: string,
        nom: string,
        region: string
      },
      geometry: {
        type: 'Polygon'
        coordinates: [CoordinatesArray[]]
      };
    }[]
  }

  return data.features.find(f => f.properties.code === code)
}

export const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371 // Earth radius
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}